import {useContext, useState} from 'react';

import {useQueryClient} from '@tanstack/react-query';
import {Form, Formik} from 'formik';
import {useTranslation} from 'react-i18next';

import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {Grid} from '@mui/material';

import {CommentActions} from '../MatchActivityList/CommentActions';

import {ConfirmDeleteDialog} from '../../../../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import {MentionComment} from '../../../../../../components/MentionListener/MentionComment';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiWrite} from '../../../../../../hooks/useApiWrite';
import {Delete} from '../../../../../../lib/api/DELETE/Delete';
import {DeleteRoutes} from '../../../../../../lib/api/DELETE/DeleteRoutes';
import {Patch} from '../../../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../../../lib/api/PATCH/PatchRoutes';
import {Post} from '../../../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../../../lib/api/POST/PostRoutes';
import {MatchComment} from '../../../../../../types/MatchActivity';
import {UserMention} from '../../../../../../types/UserMention';

interface Props {
  comment?: MatchComment;
  readonly?: boolean;
  companyId: string;
  matchId: string;
  userId: string;
  userMentions: UserMention[];
}
export const CommentForm = ({companyId, matchId, comment, readonly, userId, userMentions}: Props) => {
  const {t} = useTranslation();
  const {t: tr} = useTranslation('translation', {
    keyPrefix: 'companyApplicants.applicantsCard.cardDetails.companyMatchNotes',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const editMode = !!comment;

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onEditClick = () => {
    setIsEditing(!isEditing);
  };

  const {dispatchMessage} = useContext(SnackBarContext);

  const onSuccess = (action: 'create' | 'update' | 'delete') => {
    queryClient.invalidateQueries({queryKey: [`match-activity-${matchId}`]});
    setIsEditing(false);
    dispatchMessage(tr(`snackbar.${action}.success`), 'success');
  };

  const onError = (action: 'create' | 'update' | 'delete') => {
    dispatchMessage(tr(`snackbar.${action}.failure`), 'error');
  };

  const {makeRequest: createComment} = useApiWrite<Post.CreateMatchComment>({
    method: 'post',
    path: PostRoutes[Post.CreateMatchComment](companyId, matchId),
    onSuccess: () => onSuccess('create'),
    onError: () => onError('create'),
  });

  const {makeRequest: editComment} = useApiWrite<Patch.UpdateMatchComment>({
    method: 'patch',
    path: PatchRoutes[Patch.UpdateMatchComment](companyId, matchId, comment?.activityContent.resourceId || ''),
    onSuccess: () => onSuccess('update'),
    onError: () => onError('update'),
  });

  const {makeRequest: deleteComment} = useApiWrite<Delete.DeleteMatchComment>({
    method: 'delete',
    path: DeleteRoutes[Delete.DeleteMatchComment](companyId, matchId, comment?.activityContent.resourceId || ''),
    onSuccess: () => onSuccess('delete'),
    onError: () => onError('delete'),
  });

  const handleSubmit = (comment: string) => {
    if (editMode) {
      return editComment({
        matchComment: {
          content: comment,
        },
      });
    } else {
      return createComment({
        matchComment: {
          content: comment,
          authorId: userId,
          authorType: 'CompanyEmployee',
          visibility: 'external',
        },
      });
    }
  };

  const handleDelete = () => {
    deleteComment(undefined);
  };

  if (readonly) {
    return (
      <MentionComment
        readonly
        availableUserMentions={userMentions}
        initialValue={comment?.activityContent.content}
        commentId={comment?.activityContent.resourceId}
      />
    );
  }

  if (editMode && !isEditing) {
    return (
      <Grid>
        <MentionComment
          readonly
          availableUserMentions={userMentions}
          initialValue={comment?.activityContent.content}
          commentId={comment.activityContent.resourceId}
        />
        <Grid mt={1}>
          <CommentActions onEdit={onEditClick} onDelete={openModal} isEditing={isEditing} />
        </Grid>
        <ConfirmDeleteDialog
          handleConfirm={handleDelete}
          handleCancel={closeModal}
          open={isModalOpen}
          dialogContent={t('companyApplicants.applicantsCard.cardDetails.companyMatchNotes.deleteComment.modalText')}
        />
      </Grid>
    );
  }

  return (
    <Grid marginTop={3}>
      <Formik
        initialValues={{
          matchNote: comment?.activityContent.content || '',
        }}
        onSubmit={(values, {setSubmitting, setValues}) => {
          handleSubmit(values.matchNote).then(() => {
            setValues({matchNote: ''});
          });
          setSubmitting(false);
        }}
      >
        {({isSubmitting, values, setFieldValue}) => (
          <Form style={{width: '100%'}}>
            <MentionComment
              availableUserMentions={userMentions}
              shouldReset={!isSubmitting && !values.matchNote}
              initialValue={comment?.activityContent.content}
              onChange={(value: string) => {
                setFieldValue('matchNote', value);
              }}
            />
            <Grid container justifyContent='space-between' wrap='nowrap' mt={1}>
              {editMode ? (
                <CommentActions onEdit={onEditClick} onDelete={openModal} isEditing={isEditing} />
              ) : (
                <Grid minWidth='30px'></Grid>
              )}
              <EmpionButton
                type='submit'
                disabled={isSubmitting || !values.matchNote}
                startIcon={<SaveOutlinedIcon fontSize='small' />}
                sx={{fontSize: '15px', lineHeight: '22px', minHeight: '34px', px: 1.5}}
              >
                {t('shared.formButtons.save')}
              </EmpionButton>
            </Grid>
          </Form>
        )}
      </Formik>
      <ConfirmDeleteDialog
        handleConfirm={handleDelete}
        handleCancel={closeModal}
        open={isModalOpen}
        dialogContent={t('companyApplicants.applicantsCard.cardDetails.companyMatchNotes.deleteComment.modalText')}
      />
    </Grid>
  );
};
